/**
 * Görünüm çubuğu — ızgara/liste ve kart boyutu seçimi.
 *
 * ## Neden ayrı bir bileşen?
 *
 * Aynı kontrol iki yerde gerekiyor (katalog ve "Son Eklenenler" bölümü) ve
 * ileride ana sayfada da kullanılacak. Erişilebilir işaretleme burada
 * bir yerde tanımlanır: seçenek grubu `radiogroup`, boyut `radiogroup`,
 * liste düğmesi `aria-pressed` ile.
 *
 * ## Erişilebilirlik notları
 *
 *   - Seçim grupları `<fieldset>` + `<legend>`. `role="radiogroup"` yerine gerçek
 *     öğe: hem ekran okuyucu hem de klavye gezinmesi (ok tuşlarıyla grup içinde
 *     gezinme) tarayıcı tarafından bedava sağlanır ve `button` + `role="radio"`
 *     için elle yazılması gerekirdi.
 *   - Seçici, görünür düğmenin **içinde** gizli bir `<input type="radio">`.
 *     Tıklanabilir alan `<label>` olur; dolayısıyla hedef alan 30 px yüksekliğinde
 *     (WCAG 2.2 hedef boyutu) ve fare imleci doğru yerde.
 *   - Boyut seçicisi **yalnız ızgara modunda** görünür. Liste modunda kart boyutu
 *     zaten sabit; görünmeyen bir kontrolü göstermek yanıltıcıdır.
 *   - `legend` görsel olarak gizlenir (`visually-hidden`), ekran okuyucu okur.
 */
import type { ReactNode } from 'react';
import {
  type CardSize,
  MODE_LABELS,
  SIZE_LABELS,
  type ViewMode,
  type ViewPrefsApi,
} from '../viewPrefs.ts';

const SIZES: CardSize[] = ['sm', 'md', 'lg'];

export function ViewToolbar({ view }: { view: ViewPrefsApi }): ReactNode {
  const { prefs, isList } = view;

  return (
    <div className="view-toolbar">
      <fieldset className="view-toolbar__group">
        <legend className="visually-hidden">Görünüm biçimi</legend>
        {(['grid', 'list'] as ViewMode[]).map((mode) => (
          <label
            key={mode}
            className={`view-toolbar__btn ${prefs.mode === mode ? 'is-active' : ''}`}
            title={mode === 'grid' ? 'Izgara görünümü' : 'Liste görünümü'}
          >
            <input
              type="radio"
              name="pbfork-view-mode"
              value={mode}
              checked={prefs.mode === mode}
              onChange={() => view.setMode(mode)}
              className="visually-hidden"
            />
            {mode === 'grid' ? <GridIcon /> : <ListIcon />}
            <span className="view-toolbar__btn-text">{MODE_LABELS[mode]}</span>
          </label>
        ))}
      </fieldset>

      {!isList && (
        <fieldset className="view-toolbar__group">
          <legend className="visually-hidden">Kart boyutu</legend>
          {SIZES.map((size) => (
            <label
              key={size}
              className={`view-toolbar__size ${prefs.size === size ? 'is-active' : ''}`}
              title={`${SIZE_LABELS[size]} kartlar`}
            >
              <input
                type="radio"
                name="pbfork-view-size"
                value={size}
                checked={prefs.size === size}
                onChange={() => view.setSize(size)}
                className="visually-hidden"
              />
              <span className="view-toolbar__size-box" data-size={size} aria-hidden="true" />
              <span className="view-toolbar__btn-text">{SIZE_LABELS[size]}</span>
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}

/** Izgara simgesi: 2×2 kare. */
function GridIcon(): ReactNode {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
    </svg>
  );
}

/** Liste simgesi: üç yatay satır. */
function ListIcon(): ReactNode {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="3" y="5" width="18" height="2.6" rx="1.3" />
      <rect x="3" y="10.7" width="18" height="2.6" rx="1.3" />
      <rect x="3" y="16.4" width="18" height="2.6" rx="1.3" />
    </svg>
  );
}
